// SearchComponent.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    AppBar,
    Toolbar,
    IconButton,
    TextField,
    InputAdornment,
    CircularProgress,
    LinearProgress,
    Typography,
    Box,
    Paper,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingBasketOutlinedIcon from "@mui/icons-material/ShoppingBasketOutlined";
import { Link } from "react-router-dom";
import { BASE_URL_API, PRODUCT_BASE_URL_API, MARKET_BASE_URL_API } from "../Const";
import { BasketComponent } from "./BasketComponent";
import { BackButton } from "./HookUtils";

const CARD_WIDTH = 150;
const DEBOUNCE_MS = 300;

export default function SearchComponent() {
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [productGroups, setProductGroups] = useState([]);
    const [marketGroups, setMarketGroups] = useState([]);

    const debounceRef = useRef(null);
    const abortRef = useRef(null);

    /* ---------- Helpers ---------- */
    const groupByCategory = (items, key) => {
        const map = new Map();
        items.forEach((item) => {
            const catTitle = item.category?.title || "بدون دسته";
            if (!map.has(catTitle)) map.set(catTitle, []);
            map.get(catTitle).push(item);
        });
        return Array.from(map.entries())
            .map(([category, list]) => ({ category, list }))
            .sort((a, b) => b.list.length - a.list.length); // بزرگ‌ترها اول
    };

    /* ---------- Fetch / Search ---------- */
    const fetchResults = async (q) => {
        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        setLoading(true);
        try {
            const res = await fetch(`${BASE_URL_API}search?q=${encodeURIComponent(q)}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("pwa_token")}`,
                    "Content-Type": "application/json",
                },
                signal: controller.signal,
            });
            const json = await res.json();
            if (json.code === 200) {
                setProductGroups(groupByCategory(json.data.products, "product"));
                setMarketGroups(groupByCategory(json.data.markets, "market"));
            }
        } catch (err) {
            if (err.name !== "AbortError") console.error(err);
        } finally {
            setLoading(false);
        }
    };

    /* ---------- Debounced query ---------- */
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchResults(query), DEBOUNCE_MS);
        return () => clearTimeout(debounceRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query]);

    /* ---------- Render helpers ---------- */
    const ProductCard = ({ p }) => (
        <div style={{ width: CARD_WIDTH }}>
            <Link to={`/product?id=${p.id}`} style={{ textDecoration: "none" }}>
                <Paper
                    sx={{
                        width: CARD_WIDTH,
                        height: 140,
                        borderRadius: 2,
                        backgroundImage: `url(${
                            p.pictures?.length ? PRODUCT_BASE_URL_API + p.pictures[0].url : "hamekara-logo.png"
                        })`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
                <Typography
                    variant="body2"
                    noWrap
                    sx={{ mt: 0.5, textAlign: "center", color: "text.primary", fontWeight: 600 }}
                >
                    {p.title}
                </Typography>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textAlign: "center", display: "block" }}
                >
                    {p.markets?.name ?? ""}
                </Typography>
                {/* قیمت */}
                {p.del_price > 0 ? (
                    <>
                        <Typography
                            variant="caption"
                            sx={{ textDecoration: "line-through", color: "text.disabled", textAlign: "center" }}
                            display="block"
                        >
                            {Number(p.price).toLocaleString()} تومان
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: "green", textAlign: "center", fontWeight: 600 }}
                        >
                            {(p.price - p.del_price).toLocaleString()} تومان
                        </Typography>
                    </>
                ) : (
                    <Typography
                        variant="body2"
                        sx={{ color: "green", textAlign: "center", fontWeight: 600 }}
                    >
                        {p.price > 0 ? `${Number(p.price).toLocaleString()} تومان` : "تماس بگیرید."}
                    </Typography>
                )}
            </Link>
        </div>
    );

    const MarketCard = ({ m }) => (
        <div style={{ width: CARD_WIDTH }}>
            <Link to={`/market?id=${m.id}`} style={{ textDecoration: "none" }}>
                <Paper
                    sx={{
                        width: CARD_WIDTH,
                        height: 140,
                        borderRadius: 2,
                        backgroundImage: `url(${
                            m.pictures?.length ? MARKET_BASE_URL_API + m.pictures[0].url : "hamekara-logo.png"
                        })`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
                <Typography
                    variant="body2"
                    noWrap
                    sx={{ mt: 0.5, textAlign: "center", color: "text.primary", fontWeight: 600 }}
                >
                    {m.name}
                </Typography>
            </Link>
        </div>
    );

    const Section = ({ title, children }) => (
        <Box sx={{ mt: 3 }}>
            <Typography sx={{ mr: 1, fontWeight: 700 }}>{title}</Typography>
            <Box
                sx={{
                    mt: 1,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    justifyContent: "center",
                }}
            >
                {children}
            </Box>
        </Box>
    );

    /* ---------- Main Render ---------- */

    return (
        <>
            <AppBar sx={{ background: "rgba(238,238,238,0.3)" }} elevation={0}>
                <Toolbar>
                    <ShoppingBasketOutlinedIcon sx={{ color: "black", mr: 1 }} />
                    <Typography sx={{ flexGrow: 1, color: "black", fontWeight: 700 }}>Hamekara</Typography>
                    <IconButton sx={{ color: "black" }} size="large">
                        <BasketComponent />
                    </IconButton>
                    <BackButton />
                </Toolbar>
                <Box sx={{ p: 1, pb: 2 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="جستجو کالا و فروشگاه..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        InputProps={{
                            sx: { background: "rgba(232,232,232,0.5)" },
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                            endAdornment: loading && (
                                <InputAdornment position="end">
                                    <CircularProgress size={20} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
                {loading && <LinearProgress color="secondary" />}
            </AppBar>

            <Box sx={{ mt: 16, mb: 8, px: 2 }}>
                {productGroups.map((g) => (
                    <Section key={g.category} title={g.category}>
                        {g.list.map((p) => (
                            <ProductCard key={p.id} p={p} />
                        ))}
                    </Section>
                ))}

                {marketGroups.map((g) => (
                    <Section key={g.category} title={g.category}>
                        {g.list.map((m) => (
                            <MarketCard key={m.id} m={m} />
                        ))}
                    </Section>
                ))}

                {!loading && !productGroups.length && !marketGroups.length && (
                    <Typography sx={{ mt: 6, textAlign: "center", color: "text.secondary" }}>
                        نتیجه‌ای یافت نشد.
                    </Typography>
                )}
            </Box>
        </>
    );
}
